import type {
    CruciblePartyState,
    CrucibleSessionConnection,
    PartyChatMessage,
    PartyConnectRefusal,
} from '@sorrem/shared';

/**
 * Something wrong with the party, by how the gathering page answers it:
 * `session-expired` offers to log in again, `fatal` to return to the
 * character (the party is over for this member), and a `notice` (a refused
 * request, an unanswered one) is shown while the party stays connected.
 */
export type PartyIssue =
    | { readonly kind: 'session-expired'; readonly message: string }
    | { readonly kind: 'fatal'; readonly message: string }
    | { readonly kind: 'notice'; readonly message: string };

export interface PartyRealtimeState {
    crucibleId: string | null;
    partyState: CruciblePartyState | null;
    /** Whether the party socket is connected; a refused request never changes it. */
    isConnected: boolean;
    readyPayload: CrucibleSessionConnection | null;
    crucibleStartedAtMs: number | null;
    /** Local epoch ms when the ready-up countdown runs out and the run starts, or null when none is running. */
    startCountdownEndsAtMs: number | null;
    partyIssue: PartyIssue | null;
    /** Why the server just refused to start the run (#214); the party can ready up again. Null otherwise. */
    startNotice: string | null;
    chatMessages: PartyChatMessage[];
}

/** The chat lines kept; older ones drop off the front. */
export const MAX_CHAT_MESSAGES = 100;

export const INITIAL_PARTY_REALTIME_STATE: PartyRealtimeState = {
    crucibleId: null,
    partyState: null,
    isConnected: false,
    readyPayload: null,
    crucibleStartedAtMs: null,
    startCountdownEndsAtMs: null,
    partyIssue: null,
    startNotice: null,
    chatMessages: [],
};

/**
 * Everything that changes the party store: the party socket's events (by
 * their own names), the store's own steps, and the outcome of a request.
 * Events for another member (a `party:ready` or `party:member-removed`
 * meant for someone else) are filtered out before they get here.
 */
export type PartyRealtimeEvent =
    | { readonly type: 'connecting'; readonly crucibleId: string }
    | { readonly type: 'connect' }
    | { readonly type: 'disconnect' }
    | { readonly type: 'connect_error'; readonly code: PartyConnectRefusal | undefined; readonly message: string }
    | { readonly type: 'party:state'; readonly crucibleId: string; readonly partyState: CruciblePartyState; readonly nowMs: number }
    | { readonly type: 'party:started'; readonly nowMs: number }
    | { readonly type: 'party:start-refused'; readonly message: string }
    | { readonly type: 'party:ready'; readonly sessionConnection: CrucibleSessionConnection }
    | { readonly type: 'party:member-removed' }
    | { readonly type: 'party:error'; readonly message: string }
    | { readonly type: 'party:chat-message'; readonly message: PartyChatMessage }
    | { readonly type: 'issue'; readonly issue: PartyIssue };

/** The party store after `event`: pure, so every transition tests without a socket. */
export function applyPartyEvent(state: PartyRealtimeState, event: PartyRealtimeEvent): PartyRealtimeState {
    switch (event.type) {
        case 'connecting':
            return { ...INITIAL_PARTY_REALTIME_STATE, crucibleId: event.crucibleId };
        case 'connect':
            return { ...state, isConnected: true, partyIssue: null };
        case 'disconnect':
            return { ...state, isConnected: false };
        case 'connect_error':
            // Only an expired session can be fixed by logging in.
            return {
                ...state,
                isConnected: false,
                partyIssue: event.code === 'session-expired'
                    ? { kind: 'session-expired', message: event.message }
                    : { kind: 'fatal', message: event.message },
            };
        case 'party:state': {
            const remainingMs = event.partyState.startCountdownRemainingMs ?? null;
            return {
                ...state,
                crucibleId: event.crucibleId,
                partyState: event.partyState,
                isConnected: true,
                crucibleStartedAtMs: parseStartedAtMs(event.partyState.crucible.startedAt),
                // The server sends time remaining, not a timestamp, so its clock never has to match this one.
                startCountdownEndsAtMs: remainingMs === null ? null : event.nowMs + remainingMs,
                // A fresh snapshot settles a notice; a fatal issue stands.
                partyIssue: state.partyIssue?.kind === 'notice' ? null : state.partyIssue,
                // A refusal stands until the party tries again: a new countdown.
                startNotice: remainingMs === null ? state.startNotice : null,
            };
        }
        case 'party:started':
            return { ...state, crucibleStartedAtMs: event.nowMs, startCountdownEndsAtMs: null, partyIssue: null, startNotice: null };
        case 'party:start-refused':
            return { ...state, startCountdownEndsAtMs: null, startNotice: event.message };
        case 'party:ready':
            return { ...state, readyPayload: event.sessionConnection, partyIssue: null };
        case 'party:member-removed':
            return {
                ...state,
                isConnected: false,
                partyIssue: { kind: 'fatal', message: 'You were removed from the party by its creator.' },
            };
        case 'party:error':
            // The run couldn't start, or its crucible was removed: the party is over.
            return { ...state, partyIssue: { kind: 'fatal', message: event.message } };
        case 'party:chat-message': {
            const chatMessages = [...state.chatMessages, event.message];
            return {
                ...state,
                chatMessages: chatMessages.length <= MAX_CHAT_MESSAGES
                    ? chatMessages
                    : chatMessages.slice(chatMessages.length - MAX_CHAT_MESSAGES),
            };
        }
        case 'issue':
            // A refused or unanswered request is a notice; the socket's state is its own.
            return event.issue.kind === 'notice'
                ? { ...state, partyIssue: event.issue }
                : { ...state, isConnected: false, partyIssue: event.issue };
        default: {
            const unhandled: never = event;
            return unhandled;
        }
    }
}

function parseStartedAtMs(startedAt: string | null): number | null {
    if (startedAt === null) {
        return null;
    }

    const parsedMs = Date.parse(startedAt);
    return Number.isNaN(parsedMs) ? null : parsedMs;
}
